import {
  buildChildrenMap,
  descendantsOf,
  resolveAudienceAccountIds,
  type ResolverAccount,
} from './broadcastAudience.js';
import type { AudienceScope } from '../types/broadcast.js';

// Stage 6A v2 §2.1-§2.3 — pure tree-selection logic for the audience tree.
// Computes each node's tri-state checkbox and the scope transition for clicking
// a node, across a (possibly multi-brand) forest. Pure (no React) so the
// cascade rules are unit tested in isolation.
//
// Storage is LITERAL: clicking a Group/Brand writes that node AND all its
// descendants into selectedAccountIds (cascade is explicit, matching the
// no-inheritance resolver). Unticking a child under a still-selected parent
// records it in excludedAccountIds.

export type NodeSelectionState = 'checked' | 'partial' | 'unchecked';

function parentMap(accounts: ResolverAccount[]): Map<string, string | null> {
  return new Map(accounts.map((a) => [a.id, a.parentAccountId]));
}

// Is `id` under an ancestor that's still in the selected set? Used to decide
// whether unticking a leaf needs an explicit exclusion.
function coveredByAncestorSelected(
  id: string,
  parentById: Map<string, string | null>,
  selected: Set<string>,
): boolean {
  let cur = parentById.get(id) ?? null;
  while (cur) {
    if (selected.has(cur)) return true;
    cur = parentById.get(cur) ?? null;
  }
  return false;
}

// Tri-state per node, derived from the resolved audience (selected − excluded ∩
// scope). A leaf is checked iff in the audience; a node with descendants is
// checked iff ALL its descendants are in the audience, partial iff some.
export function computeNodeStates(
  accounts: ResolverAccount[],
  scope: AudienceScope,
): Map<string, NodeSelectionState> {
  const audience = new Set(resolveAudienceAccountIds(scope, accounts));
  const children = buildChildrenMap(accounts);
  const states = new Map<string, NodeSelectionState>();
  for (const a of accounts) {
    const desc = descendantsOf(a.id, children);
    if (desc.length === 0) {
      states.set(a.id, audience.has(a.id) ? 'checked' : 'unchecked');
      continue;
    }
    const inCount = desc.filter((d) => audience.has(d)).length;
    states.set(
      a.id,
      inCount === 0 ? 'unchecked' : inCount === desc.length ? 'checked' : 'partial',
    );
  }
  return states;
}

// Toggle a tree node. Nodes with descendants (Brand/Group) cascade on/off over
// their whole subtree; leaves (Location) toggle individually, recording an
// exclusion when unticked under a still-selected ancestor.
export function toggleNodeSelection(
  accountId: string,
  accounts: ResolverAccount[],
  scope: AudienceScope,
): AudienceScope {
  const childrenByParent = buildChildrenMap(accounts);
  const desc = descendantsOf(accountId, childrenByParent);
  const states = computeNodeStates(accounts, scope);
  const state = states.get(accountId) ?? 'unchecked';

  const selected = new Set(scope.selectedAccountIds);
  const excluded = new Set(scope.excludedAccountIds);

  if (desc.length > 0) {
    // Group / Brand: cascade. partial or unchecked → check all; checked → off.
    const subtree = [accountId, ...desc];
    const turnOn = state !== 'checked';
    for (const id of subtree) {
      selected.delete(id);
      excluded.delete(id);
    }
    if (turnOn) for (const id of subtree) selected.add(id);
  } else {
    // Leaf / Location.
    const parentById = parentMap(accounts);
    if (state === 'checked') {
      selected.delete(accountId);
      if (coveredByAncestorSelected(accountId, parentById, selected)) excluded.add(accountId);
    } else {
      excluded.delete(accountId);
      selected.add(accountId);
    }
  }

  return {
    ...scope,
    selectedAccountIds: [...selected],
    excludedAccountIds: [...excluded],
  };
}
